import React, { useState, useEffect, useRef } from 'react';
import { FileText, Upload, File, Image, FileSpreadsheet, AlertCircle } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const formatFileSize = (bytes) => {
  if (!bytes) return 'N/A';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
};

const getFileIcon = (mimeType) => {
  if (!mimeType) return <File size={20} />;
  if (mimeType.startsWith('image/')) return <Image size={20} />;
  if (mimeType.includes('pdf')) return <FileText size={20} />;
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return <FileSpreadsheet size={20} />;
  return <File size={20} />;
};

export default function Documents({ caseId, isAdmin }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  useEffect(() => { fetchDocuments(); }, [caseId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const result = await api.documents.list(caseId);
      setDocuments(result?.data || result || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to load documents');
    } finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    try {
      setUploading(true);
      await api.documents.upload(caseId, Array.from(files), 'Other');
      toast.success('Documents uploaded successfully');
      fetchDocuments();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload documents');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleView = async (doc) => {
    try {
      const blob = await api.documents.download(doc.id);
      const url = window.URL.createObjectURL(new Blob([blob], { type: doc.mime_type || 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      console.error('View failed:', error);
      toast.error('Failed to open document');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const blob = await api.documents.download(doc.id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_name || doc.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download document');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.original_name}"?`)) return;
    try {
      await api.documents.delete(doc.id);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete document');
    }
  };

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

  const actionBtn = (bg, color, border) => ({
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    padding: '6px 12px', fontSize: '13px', fontWeight: 500,
    border: `1px solid ${border}`, borderRadius: '6px',
    backgroundColor: bg, color: color, cursor: 'pointer', whiteSpace: 'nowrap'
  });

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Documents ({documents.length})</h3>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', backgroundColor: '#2563eb', color: 'white', borderRadius: '8px', cursor: uploading ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 500, opacity: uploading ? 0.6 : 1 }}>
          <Upload size={16} />
          {uploading ? 'Uploading...' : 'Upload Documents'}
          <input ref={fileInputRef} type="file" multiple onChange={handleUpload} disabled={uploading} style={{ display: 'none' }} />
        </label>
      </div>
      {documents.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px dashed #d1d5db' }}>
          <AlertCircle size={40} style={{ color: '#9ca3af', marginBottom: '12px' }} />
          <p style={{ color: '#6b7280', margin: 0 }}>No documents uploaded yet</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {documents.map((doc) => (
            <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                <div style={{ color: '#2563eb', flexShrink: 0 }}>{getFileIcon(doc.mime_type)}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.original_name || doc.file_name}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                    {formatFileSize(doc.file_size)}
                    {doc.document_type && doc.document_type !== 'Other' && `  ${doc.document_type}`}
                    {doc.created_at && `  ${new Date(doc.created_at).toLocaleDateString()}`}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                <button onClick={() => handleView(doc)} style={actionBtn('#eff6ff', '#2563eb', '#bfdbfe')}>View</button>
                <button onClick={() => handleDownload(doc)} style={actionBtn('#f9fafb', '#374151', '#d1d5db')}>Download</button>
                {isAdmin && (<button onClick={() => handleDelete(doc)} style={actionBtn('#fef2f2', '#dc2626', '#fca5a5')}>Delete</button>)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
